import {
  TaskStatus,
  type ConvertTaskToRecurringPayload,
  type CreateTaskPayload,
  type CreateTaskRecurrencePayload,
  type LexicalText,
  type Task,
  type UpdateTaskRecurrencePayload,
} from "@/schema"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import {
  bulkDeleteTasks,
  bulkUpdateTasks,
  convertTaskToRecurring,
  createTask,
  createTaskRecurrence,
  deactivateTaskRecurrence,
  deleteTask,
  propagateRecurrence,
  restoreTaskVersion,
  updateTask,
  updateTaskRecurrence,
  updateTaskWithFiles,
} from "./api"
import { useOptimisticTaskUpdate } from "./use-optimistic-task-update"

const BULK_DELETE_CONCURRENCY = 5

export function invalidateTaskQueries(queryClient: ReturnType<typeof useQueryClient>, task: Task) {
  queryClient.invalidateQueries({ queryKey: ["tasks"] })
  queryClient.invalidateQueries({ queryKey: ["tasks", "grouped"] })
  queryClient.invalidateQueries({ queryKey: ["tasks", "household"] })
  queryClient.invalidateQueries({ queryKey: ["tasks", "contact"] })
  queryClient.invalidateQueries({ queryKey: ["tasks", "business"] })
  queryClient.invalidateQueries({ queryKey: ["task", task.id] })
  if (task.recommendable_id) {
    queryClient.invalidateQueries({ queryKey: ["meeting", task.recommendable_id, "tasks"] })
  }
  if (task.syncbox_thread_id) {
    queryClient.invalidateQueries({ queryKey: ["syncbox_email_thread", task.syncbox_thread_id, "tasks"] })
  }
}

function invalidateProjectQueries(queryClient: ReturnType<typeof useQueryClient>, task: Task) {
  const projectId = task.project_id || task.project?.id

  if (!projectId) return

  queryClient.invalidateQueries({ queryKey: ["project", projectId] })
  queryClient.invalidateQueries({ queryKey: ["projects"] })
}

interface CreateTaskMutationParams {
  task: CreateTaskPayload
  overrideBookId?: string
}

export function useCreateTask() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ task, overrideBookId }: CreateTaskMutationParams) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createTask(context, task, overrideBookId)
    },
    meta: { errorToast: { fallback: "Failed to create task" } },
    onSuccess: (data: Task, { task }) => {
      invalidateTaskQueries(queryClient, data)
      if (task.schwab_activity_id) {
        queryClient.invalidateQueries({ queryKey: ["schwab_activity", task.schwab_activity_id] })
      }
    },
  })
}

interface CreateTaskRecurrenceMutationParams {
  recurrence: CreateTaskRecurrencePayload
  overrideBookId?: string
}

export function useCreateTaskRecurrence() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ recurrence, overrideBookId }: CreateTaskRecurrenceMutationParams) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createTaskRecurrence(context, recurrence, overrideBookId)
    },
    meta: { errorToast: { fallback: "Failed to create recurring task" } },
    onSuccess: (task: Task, { recurrence }) => {
      invalidateTaskQueries(queryClient, task)
      if (recurrence.schwab_activity_id) {
        queryClient.invalidateQueries({ queryKey: ["schwab_activity", recurrence.schwab_activity_id] })
      }
    },
  })
}

export function useConvertTaskToRecurring() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ taskId, data }: { taskId: string; data: ConvertTaskToRecurringPayload }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return convertTaskToRecurring(context, taskId, data)
    },
    meta: { errorToast: { fallback: "Failed to make task recurring" } },
    onSuccess: (task: Task) => {
      invalidateTaskQueries(queryClient, task)
    },
  })
}

interface DeactivateTaskRecurrenceParams {
  recurrenceId: string
}

export function useDeactivateTaskRecurrence() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ recurrenceId }: DeactivateTaskRecurrenceParams) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return deactivateTaskRecurrence(context, recurrenceId)
    },
    meta: { errorToast: { fallback: "Failed to stop task recurrence" } },
    onSuccess: (_data, { recurrenceId }) => {
      // A task detail query can be keyed by a notification lookup id that differs
      // from task.id. Update every matching detail cache before refetching them.
      const deactivatedAt = new Date().toISOString()
      queryClient.setQueriesData<Task>({ queryKey: ["task"] }, (existing) =>
        existing?.task_recurrence?.id === recurrenceId
          ? {
              ...existing,
              task_recurrence: {
                ...existing.task_recurrence,
                active: false,
                deactivated_at: existing.task_recurrence.deactivated_at ?? deactivatedAt,
              },
            }
          : existing
      )
      queryClient.invalidateQueries({ queryKey: ["task"] })
      queryClient.invalidateQueries({ queryKey: ["tasks"] })
      queryClient.invalidateQueries({ queryKey: ["tasks", "household"] })
      queryClient.invalidateQueries({ queryKey: ["tasks", "contact"] })
      queryClient.invalidateQueries({ queryKey: ["tasks", "business"] })
    },
  })
}

export function useUpdateTaskRecurrence() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ recurrenceId, data }: { recurrenceId: string; data: UpdateTaskRecurrencePayload }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateTaskRecurrence(context, recurrenceId, data)
    },
    meta: { errorToast: { fallback: "Failed to update repeating cadence" } },
    onSuccess: (task: Task) => {
      invalidateTaskQueries(queryClient, task)
    },
  })
}

export function usePropagateRecurrence() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (taskId: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return propagateRecurrence(context, taskId)
    },
    meta: { errorToast: { fallback: "Failed to apply changes to series" } },
    onSuccess: (task: Task) => {
      invalidateTaskQueries(queryClient, task)
    },
  })
}

export function useUpdateTask() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  const optimisticUpdate = useOptimisticTaskUpdate()

  return useMutation({
    mutationFn: async (taskData: Partial<Task> & { id: string }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateTask(context, taskData)
    },
    onMutate: async (newTask) => {
      return optimisticUpdate(newTask, {
        targetQueryKeys: [
          ["task", newTask.id],
          ["tasks"],
          ["tasks", "grouped"],
          ["tasks", "household"],
          ["tasks", "contact"],
          ["tasks", "business"],
        ],
      })
    },
    onError: (_error, _variables, context) => {
      if (context) {
        Object.entries(context).forEach(([queryKeyStr, data]) => {
          if (data) {
            const queryKey = JSON.parse(queryKeyStr)
            queryClient.setQueryData(queryKey, data)
          }
        })
      }
    },
    onSuccess: (updatedTask, taskData) => {
      queryClient.setQueryData(["task", updatedTask.id], updatedTask)
      invalidateTaskQueries(queryClient, updatedTask)
      invalidateProjectQueries(queryClient, updatedTask)
      if (taskData.title || taskData.description) {
        queryClient.invalidateQueries({ queryKey: ["task-versions", updatedTask.id] })
        queryClient.invalidateQueries({ queryKey: ["task-version", updatedTask.id] })
      }
    },
  })
}

export function useRestoreTaskVersion(taskId: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (versionId: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return restoreTaskVersion(context, taskId, versionId)
    },
    meta: { errorToast: { fallback: "Failed to restore task version" } },
    onSuccess: (task) => {
      invalidateTaskQueries(queryClient, task)
      queryClient.invalidateQueries({ queryKey: ["task-versions", taskId] })
      queryClient.invalidateQueries({ queryKey: ["task-version", taskId] })
    },
  })
}

interface CompleteTaskPayload {
  id: string
  content?: LexicalText
  outcome_id?: string
  project_comment?: LexicalText
  form_responses?: Record<string, string | number | boolean | null>
}

export function useCompleteTask() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: string | CompleteTaskPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      if (typeof payload === "string") {
        return updateTask(context, { id: payload, status: TaskStatus.DONE })
      } else {
        return updateTask(context, {
          id: payload.id,
          status: TaskStatus.DONE,
          ...(payload.content && { content: payload.content }),
          ...(payload.outcome_id && { outcome_id: payload.outcome_id }),
          ...(payload.project_comment && { project_comment: payload.project_comment }),
          ...(payload.form_responses && { form_responses: payload.form_responses }),
        })
      }
    },
    meta: {
      errorToast: {
        message: (error) =>
          error.message === "Outcome selection is required" || error.message === "Form fields are required"
            ? ""
            : error.message,
        fallback: "",
      },
    },
    onSuccess: (updatedTask, payload) => {
      if (typeof payload !== "string" && payload.form_responses) {
        queryClient.invalidateQueries({ queryKey: ["household", updatedTask.household_id] })
        queryClient.invalidateQueries({ queryKey: ["households"] })
      }
      const removeCompletedTaskFromDashboard = () => {
        queryClient.setQueriesData<Task[]>({ queryKey: ["tasks", "dashboard"] }, (tasks) =>
          tasks?.filter((task) => task.id !== updatedTask.id)
        )
      }

      // Dashboard queries only contain incomplete tasks, so remove the completed task immediately.
      // Apply the removal again after refetching in case a lagging read replica returns the old row.
      removeCompletedTaskFromDashboard()

      // Refresh tasks before the project without delaying mutation settlement.
      // The originating task card can unmount when the milestone changes.
      void queryClient.invalidateQueries({ queryKey: ["tasks"] }).then(() => {
        removeCompletedTaskFromDashboard()
        invalidateProjectQueries(queryClient, updatedTask)
      })
      queryClient.invalidateQueries({ queryKey: ["task", updatedTask.id] })
      if (updatedTask.recommendable_id) {
        queryClient.invalidateQueries({ queryKey: ["meeting", updatedTask.recommendable_id, "tasks"] })
      }
      if (updatedTask.syncbox_thread_id) {
        queryClient.invalidateQueries({ queryKey: ["syncbox_email_thread", updatedTask.syncbox_thread_id, "tasks"] })
      }
      queryClient.invalidateQueries({ queryKey: ["notetakerTasks"] })
    },
  })
}

export function useUncompleteTask() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: string | { id: string; content: LexicalText }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      const id = typeof payload === "string" ? payload : payload.id
      return updateTask(context, { id, status: TaskStatus.TODO })
    },
    onSuccess: (updatedTask) => {
      invalidateTaskQueries(queryClient, updatedTask)
      invalidateProjectQueries(queryClient, updatedTask)
      queryClient.invalidateQueries({ queryKey: ["notetakerTasks"] })
    },
  })
}

export function useDeleteTask() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return deleteTask(context, id)
    },
    meta: { errorToast: { fallback: "Failed to delete task" } },
    onSuccess: (deletedTask) => {
      if (deletedTask) {
        invalidateTaskQueries(queryClient, deletedTask)
        invalidateProjectQueries(queryClient, deletedTask)
      } else {
        queryClient.invalidateQueries({ queryKey: ["tasks"] })
      }
      queryClient.invalidateQueries({ queryKey: ["meeting"] })
      queryClient.invalidateQueries({ queryKey: ["meetings"] })
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "syncbox_email_thread" &&
          query.queryKey[2] === "tasks",
      })
    },
  })
}

export function useBulkUpdateTasks() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      task_ids,
      status,
      assigned_to_id,
      assigned_team_id,
      deadline_at,
      priority,
      tag_ids,
    }: {
      task_ids: string[]
      status?: TaskStatus
      assigned_to_id?: string | null
      assigned_team_id?: string | null
      deadline_at?: string | null
      priority?: string | null
      tag_ids?: string[]
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return bulkUpdateTasks(context, {
        task_ids,
        status,
        assigned_to_id,
        assigned_team_id,
        deadline_at,
        priority,
        tag_ids,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] })
      queryClient.invalidateQueries({ queryKey: ["tasks", "household"] })
      queryClient.invalidateQueries({ queryKey: ["tasks", "contact"] })
      queryClient.invalidateQueries({ queryKey: ["tasks", "business"] })
      queryClient.invalidateQueries({ queryKey: ["meeting"] })
      queryClient.invalidateQueries({ queryKey: ["syncbox_email_thread"] })
    },
  })
}

export function useBulkDeleteTasks() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ task_ids, onProgress }: { task_ids: string[]; onProgress?: (completed: number) => void }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      let nextTaskIndex = 0
      let completed = 0
      let deletionError: unknown

      async function deleteNextTask() {
        while (nextTaskIndex < task_ids.length && !deletionError) {
          const taskId = task_ids[nextTaskIndex]
          nextTaskIndex += 1

          try {
            await bulkDeleteTasks(context, { task_ids: [taskId] })
            completed += 1
            onProgress?.(completed)
          } catch (error) {
            deletionError = error
          }
        }
      }

      const workerCount = Math.min(BULK_DELETE_CONCURRENCY, task_ids.length)
      await Promise.all(Array.from({ length: workerCount }, deleteNextTask))

      if (deletionError) throw deletionError

      return { deleted_ids: task_ids }
    },
    meta: { errorToast: { fallback: "Failed to delete tasks" } },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["tasks"] }),
        queryClient.invalidateQueries({ queryKey: ["tasks", "household"] }),
        queryClient.invalidateQueries({ queryKey: ["tasks", "contact"] }),
        queryClient.invalidateQueries({ queryKey: ["tasks", "business"] }),
      ])
    },
  })
}

export function useUpdateTaskWithFiles() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      files,
      existingSignedIds = [],
    }: {
      id: string
      files: File[]
      existingSignedIds?: string[]
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateTaskWithFiles(context, id, files, existingSignedIds)
    },
    meta: { errorToast: { fallback: "Failed to upload file" } },
    onSuccess: (updatedTask) => {
      invalidateTaskQueries(queryClient, updatedTask)
    },
  })
}
